import { Flex, Checkbox, Input, FlexProps } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { ExtraServicesSelector } from 'store/selectors';
import { ChangeEvent, useEffect, useState } from 'react';
import { updateExtraServices } from 'store/answers/answersSlice';
import { AnimatePresence, motion } from 'framer-motion';
import { useVendorConfig } from 'store/vendor/useVendorConfig';

const MotionInput = motion(Input);

const OTHER_SERVICE_ID = 'other';

type Props = FlexProps;

const ExtraServicesList = (props: Props) => {
  const vendorConfig = useVendorConfig();
  const dispatch = useDispatch();
  const servicesAvailable = vendorConfig?.extraServices.serviceList;
  const [otherServiceChecked, setOtherServiceChecked] =
    useState<boolean>(false);
  const [otherServiceValue, setOtherServiceValue] = useState<string>('');
  const servicesSelected = useSelector(ExtraServicesSelector);

  /**
   * Adds a service to the list of selected services, or updates its label if it already exists.
   * @param serviceId The ID of the service to add or update.
   * @param label The new label for the service.
   */
  const addOrUpdateService = (serviceId: string, label?: string) => {
    const service = {
      id: serviceId,
      label:
        servicesAvailable.find((s) => s.id === serviceId)?.label || label || '',
    };
    // case service already exist and we remove so we can update
    const newServices = [...servicesSelected].filter((s) => s.id !== serviceId);
    newServices.push(service);
    dispatch(updateExtraServices(newServices));
  };

  /**
   * Removes a service from the list of selected services.
   * @param serviceId The ID of the service to remove.
   */
  const removeService = (serviceId: string) => {
    dispatch(
      updateExtraServices(servicesSelected.filter((s) => s.id !== serviceId))
    );
  };

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const serviceId = event.target.value;
    if (event.target.checked) {
      addOrUpdateService(serviceId);
    } else {
      removeService(serviceId);
    }
  };

  const handleOtherTextOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    const otherValue = event.target.value;
    setOtherServiceValue(otherValue);
    addOrUpdateService(OTHER_SERVICE_ID, otherValue);
  };

  const handleOtherCheckboxOnChange = (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    setOtherServiceChecked(event.target.checked);
    if (!event.target.checked) {
      removeService(OTHER_SERVICE_ID);
    } else {
      addOrUpdateService(OTHER_SERVICE_ID, otherServiceValue);
    }
  };

  // --------------------- effects ------------------------

  // at start, check if servicesSelected (saved) contains any services that are not in the vendorConfig.extraServices.serviceList
  // if so, this is the "other" service input value
  useEffect(() => {
    const otherService = servicesSelected.find(
      (s) => s.id === OTHER_SERVICE_ID
    );
    if (otherService) {
      setOtherServiceChecked(true);
      setOtherServiceValue(otherService.label);
    }
  }, [servicesSelected, servicesAvailable]);

  // --------------------- render ------------------------

  return (
    <Flex
      bg="transparent"
      flexDir="column"
      px={{ base: 4, md: 16 }}
      gap={4}
      width="full"
      fontSize="16px"
      fontFamily="Primary Regular"
      fontWeight={500}
      {...props}
    >
      {servicesAvailable?.map(({ id, label }) => (
        <Checkbox
          key={id}
          defaultChecked={servicesSelected.some((s) => s.id === id)}
          width="full"
          height="full"
          onChange={handleCheckboxChange}
          value={id}
          userSelect="none"
        >
          {label}
        </Checkbox>
      ))}
      {vendorConfig?.extraServices.otherService.activated && (
        <Checkbox
          key="other checkbox"
          width="full"
          height="full"
          isChecked={otherServiceChecked}
          onChange={handleOtherCheckboxOnChange}
          userSelect="none"
        >
          {vendorConfig?.extraServices.otherService.label}
        </Checkbox>
      )}
      <AnimatePresence>
        {otherServiceChecked && (
          <MotionInput
            key="other"
            mt="10px"
            border="solid 1px"
            borderColor="primaryColor"
            onChange={handleOtherTextOnChange}
            value={otherServiceValue}
            _hover={{
              borderColor: 'primaryColor_hover',
            }}
            _focus={{
              borderColor: 'primaryColor_active',
            }}
            initial={{ opacity: 0, height: 0 }}
            animate={{
              opacity: 1,
              height: '50px',
              transition: { duration: 0.3, ease: 'easeOut' },
            }}
            exit={{
              opacity: 0,
              height: 0,
              transition: { duration: 0.2, ease: 'easeIn' },
            }}
          />
        )}
      </AnimatePresence>
    </Flex>
  );
};

export default ExtraServicesList;
